import { createTheme, ThemeOptions } from '@mui/material';
import { deepOrange, amber, grey } from '@mui/material/colors';

/**
 * MUI Color Tool:
 * https://m2.material.io/inline-tools/color/
 */

// We use just one font family.
const fontFamily = 'Roboto';

const headerLineHeight = 1.2;
const headerLetterSpacing = '0.0075em';

const textLineHeight = 1.45;
const textLetterSpacing = '0.00938em';

/** Primary Blue */
const blue500 = '#00a1ce';

const red600 = '#df3222';

const grey75 = '#fcfcfc';

const blackAlphaBlend = 'rgba(0, 0, 0, 0.87)';

const primaryAlphaBlend = 'rgba(0, 161, 206, 0.15)';

export const themeOptions = {
  components: {
    MuiButton: {
      styleOverrides: {
        // TODO: 'ownerState' callback function is deprecated, replace with 'variants'
        root: ({ ownerState }) => {
          switch (ownerState.color) {
            case 'success':
              return {
                '&.Mui-disabled': {
                  // 'success' darkened via https://m2.material.io/inline-tools/color/
                  background: '#00796a',
                  color: 'rgba(0, 0, 0, 0.38)'
                }
              };
            default:
              return;
          }
        }
      }
    },
    MuiDialogContentText: {
      defaultProps: {
        sx: {
          color: blackAlphaBlend
        }
      }
    },
    MuiDialogTitle: {
      defaultProps: {
        // TODO - revisit pending standard body text size?
        variant: 'h2'
      }
    },
    MuiFab: {
      defaultProps: {
        size: 'medium'
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        // TODO - discuss label font size and possible fix
        // Note: there's a bug with label size and the outline box:
        // https://github.com/mui/material-ui/issues/31146
        // When shrink is applied to the input label (IE: Outline label is floating), then increase the fontSize and
        // top margin
        shrink: ({ ownerState }) => ({
          ...(ownerState.shrink && {
            fontSize: '1.000rem',
            top: -1
          })
        })
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: (props) => props.color === 'primary',
              style: {
                '&:hover': { backgroundColor: primaryAlphaBlend }
              }
            }
          ]
        }
      }
    },
    MuiLink: {
      defaultProps: {
        variant: 'body1'
      }
    },
    MuiOutlinedInput: {
      defaultProps: {
        sx: {
          // Increase fontSize of notched outline to be same as fontSize of input label when floating
          '& fieldset.MuiOutlinedInput-notchedOutline': {
            fontSize: '1.000rem'
          }
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body1Bold: 'p',
          subtitle2Italic: 'p'
        }
      }
    }
  },
  typography: {
    // Set the default font for the Typography component.
    fontFamily,
    // -- Extended MUI Typography Variants -- //
    h1: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    h3: {
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    h5: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    h6: {
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    button: {
      fontSize: '0.875rem'
    },
    caption: {
      fontSize: '0.75rem'
    },
    overline: {
      fontSize: '0.75rem'
    },
    // -- Custom Typography Variants -- //
    body1Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    subtitle2Italic: {
      fontSize: '0.875rem',
      fontWeight: 400,
      fontStyle: 'italic',
      lineHeight: textLineHeight,
      letterSpacing: textLetterSpacing
    },
    actionBarHeader: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: headerLineHeight,
      letterSpacing: headerLetterSpacing
    }
  },
  palette: {
    action: {
      active: blackAlphaBlend,
      hover: 'rgba(0, 0, 0, 0.09)',
      hoverOpacity: 0.15
    },
    background: {
      default: grey[50]
    },
    /**
     * Static colors for our calendar, ie: window icons
     */
    calendar: {
      guaranteed: '#009687',
      warranty: '#24659b',
      onCall: grey[900],
      techAssign: grey[900],
      noTimeFrame: '#df3222',
      notWaivable: amber['A400']
    },
    /**
     * Static dark green color for our Call Pop Call Pane
     * The call pane colors are NOT a full PaletteColor
     */
    callPane: {
      main: '#005a73',
      contrastText: grey75
    },
    cancel: {
      light: grey[50],
      main: grey75,
      dark: grey[200],
      contrastText: blackAlphaBlend
    },
    /**
     * Static colors to describe "Caution" to our users
     */
    caution: {
      yellow: '#ffe69b'
    },
    error: {
      main: red600,
      contrastText: grey75
    },
    FilledInput: {
      bg: 'rgba(0, 0, 0, 0.02)',
      hoverBg: 'rgba(0, 0, 0, 0.05)',
      disabledBg: 'rgba(0, 0, 0, 0.08)'
    },
    infoSecondary: {
      main: '#7f46e7'
    },
    primary: {
      main: blue500,
      contrastText: grey75
    },
    primaryInverted: {
      main: grey75,
      dark: grey[200],
      light: grey[50],
      contrastText: blue500
    },
    // We use secondary as an accent color which has colors in common with warning. This allows
    // us to be able to diverge the warning and success colors on future palettes.
    secondary: {
      main: amber[200]
    },
    // Override the success color in the default palette. Add generated dark/light colors.
    // See: https://material.io/resources/color/#!/?view.left=0&view.right=1&primary.color=009688
    success: {
      main: '#009687'
    },
    text: {
      primary: blackAlphaBlend
    },
    // Override the warning color in the default palette with accent colors from the legacy app.
    warning: {
      main: deepOrange[400],
      contrastText: grey75
    },
    warningExtras: { lighter: deepOrange[50] }
  },
  mixins: {
    toolbar: {
      variant: 'dense',
      height: '48px'
    }
  }
} satisfies ThemeOptions;

export const theme = createTheme({ ...themeOptions, cssVariables: true });
